import Cookies from "js-cookie";
import db from "../db/database";
import enviroment from "../config/env";
import { crearRegistro } from "../utils/registro";

const registro = crearRegistro("entorno");

// Inicializar la tabla de entorno si no existe
export const initializeEnvironmentTable = async () => {
  try {
    // Verificar si la tabla existe y tiene datos
    const count = await db.environment.count();
    if (count === 0) {
      // Crear configuración por defecto
      // El id de empresa se siembra desde el .env junto al nombre, porque en una
      // instalación nueva los dos salen de ahí y son coherentes entre sí. A
      // partir de ese momento manda la base local: si alguien cambia la empresa
      // desde Configuración, el id queda vacío y se resuelve contra Business
      // Central por el nombre. Ver resolverCompanyId en businessCentralUtils.
      const defaultEnv = {
        company: enviroment.company,
        companyId: enviroment.companyId || null,
        env: enviroment.env,
        apiUrl: enviroment.apiUrl,
        country: enviroment.country,
        isActive: true,
        lastModified: new Date(),
      };

      await db.environment.add(defaultEnv);
      registro.log("Configuración de entorno por defecto creada");
    }
  } catch (error) {
    registro.error("Error inicializando tabla de entorno:", error);
  }
};

// Obtener la configuración activa del entorno
export const getActiveEnvironment = async () => {
  try {
    // Asegurar que la tabla esté inicializada
    await initializeEnvironmentTable();

    // Obtener todas las configuraciones y filtrar la activa
    const allEnvs = await db.environment.toArray();
    const activeEnv = allEnvs.find((env) => env.isActive === true);

    if (!activeEnv) {
      throw new Error("No se encontró configuración de entorno activa");
    }

    return activeEnv;
  } catch (error) {
    registro.error("Error al obtener configuración de entorno:", error);
    throw error;
  }
};

/**
 * Guarda el id de empresa resuelto en la configuración activa.
 *
 * Se llama una sola vez por instalación —la primera que una API lo necesita— y
 * a partir de ahí el id sale de la base local como cualquier otro dato del
 * entorno. No crea una configuración nueva: completa la que ya está activa.
 */
export const guardarCompanyIdActivo = async (companyId, paraEntorno = null) => {
  try {
    const activeEnv = await getActiveEnvironment();
    // Sólo en el entorno para el que se resolvió. Si mientras se buscaba alguien
    // cambió de entorno, guardarlo en el activo le pondría el id de otro
    // ambiente: es como un terminal de Perú acabó con el id de
    // Corp_Santillana_5_Test_28 estando en Santillana_Peru (27/10/2026).
    if (
      paraEntorno &&
      (activeEnv.env !== paraEntorno.env || activeEnv.company !== paraEntorno.company)
    ) {
      registro.warn("El entorno cambió mientras se resolvía el id de empresa: no se guarda");
      return false;
    }
    await db.environment.update(activeEnv.id, { companyId, lastModified: new Date() });
    return true;
  } catch (error) {
    registro.error("No se pudo guardar el id de empresa en la configuración:", error);
    return false;
  }
};

/**
 * Olvida el id de empresa guardado, para que la próxima llamada lo vuelva a
 * resolver por el nombre. Se usa cuando Business Central contesta que ese id no
 * existe en el entorno: el id guardado es de otro ambiente, o el ambiente se
 * volvió a crear.
 */
export const olvidarCompanyIdActivo = async () => {
  try {
    const activeEnv = await getActiveEnvironment();
    await db.environment.update(activeEnv.id, { companyId: null, lastModified: new Date() });
    return true;
  } catch (error) {
    registro.error("No se pudo olvidar el id de empresa de la configuración:", error);
    return false;
  }
};

// Guardar nueva configuración de entorno
export const saveEnvironment = async (environmentData) => {
  try {
    // Asegurar que la tabla esté inicializada
    await initializeEnvironmentTable();

    // Desactivar todas las configuraciones existentes
    const allEnvs = await db.environment.toArray();

    for (const env of allEnvs) {
      if (env.isActive) {
        await db.environment.update(env.id, { isActive: false });
      }
    }

    // Añadir la nueva configuración como activa
    const newEnv = {
      ...environmentData,
      isActive: true,
      lastModified: new Date(),
    };

    const id = await db.environment.add(newEnv);
    registro.log(
      `Configuración guardada: empresa ${newEnv.company}, entorno ${newEnv.env} (id ${id})`
    );

    return { ...newEnv, id };
  } catch (error) {
    registro.error("Error al guardar configuración de entorno:", error);
    registro.error("Stack trace:", error.stack);
    throw error;
  }
};

// Limpiar toda la base de datos excepto la configuración de entorno
export const clearDatabaseData = async () => {
  try {
    registro.warn("Vaciando las tablas locales; sólo se conserva la configuración de entorno");

    // Todas las tablas del esquema salvo environment. Sale del propio Dexie y
    // no de una lista escrita a mano: esa lista se quedaba atrás con cada tabla
    // nueva (formasPagoPorTienda, grupoCajeros, puestos, ventasEnCurso no se
    // limpiaban), y al cambiar de entorno quedaban datos de la empresa anterior.
    const tablesToClear = db.tables
      .map((table) => table.name)
      .filter((tableName) => tableName !== "environment");

    // Limpiar cada tabla
    for (const tableName of tablesToClear) {
      await db.table(tableName).clear();
    }

    registro.log(`Tablas vaciadas: ${tablesToClear.length}`);
    return true;
  } catch (error) {
    registro.error("Error al limpiar base de datos:", error);
    throw error;
  }
};

/**
 * Lo que se perdería al borrar la base local: lo que todavía no llegó a
 * Business Central. Sólo para avisar antes de borrar; no bloquea.
 *
 * Las facturas se cuentan con el mismo criterio que el listado de pendientes
 * (sin borradores ni borradas); aperturas, cierres y clientes por su
 * `pendingSync`.
 */
export const contarLoQueNoSeSincronizo = async () => {
  const contar = async (nombre, consulta) => {
    try {
      return await consulta(db.table(nombre));
    } catch (error) {
      // Una tabla que no se puede leer no impide borrar: es justo el caso de
      // una base rota que se quiere levantar de cero.
      registro.warn(`No se pudieron contar los pendientes de ${nombre}:`, error);
      return 0;
    }
  };
  const porPendingSync = (tabla) => tabla.where("pendingSync").equals(1).count();

  return {
    documentos: await contar("invoices", (tabla) =>
      tabla
        .filter((f) => !f.deleted && !f.isDraft && Boolean(f.pendingSync || f.syncError))
        .count()
    ),
    aperturas: await contar("aperturaDeCaja", porPendingSync),
    cierres: await contar("cierreDeCaja", porPendingSync),
    clientes: await contar("clients", porPendingSync),
  };
};

/**
 * Borra la base local entera (la IndexedDB) para levantarla de cero.
 *
 * A diferencia de `clearDatabaseData`, no conserva nada: tampoco los entornos
 * guardados. Al recargar, Dexie la vuelve a crear con el esquema actual e
 * `initializeEnvironmentTable` siembra el entorno del `.env`. Sirve para un
 * cambio de ambiente hecho desde el despliegue, o para una base que quedó en un
 * estado que vaciar tablas no arregla.
 *
 * También se borra la sesión: la cookie apunta a un usuario que deja de
 * existir. Quien llama tiene que recargar la página.
 */
export const borrarBaseLocal = async () => {
  registro.warn("Se borra la base local completa (IndexedDB)");
  db.close();
  await db.delete();
  Cookies.remove("user");
  registro.log("Base local borrada");
};

// Validar configuración de entorno
export const validateEnvironment = (environmentData) => {
  const requiredFields = ["company", "env", "apiUrl", "country"];

  for (const field of requiredFields) {
    if (!environmentData[field] || environmentData[field].trim() === "") {
      throw new Error(`El campo ${field} es obligatorio`);
    }
  }

  // Validar URLs
  const urlFields = ["apiUrl"];
  for (const field of urlFields) {
    try {
      new URL(environmentData[field]);
    } catch {
      throw new Error(`El campo ${field} debe ser una URL válida`);
    }
  }

  return true;
};

// Obtener todas las configuraciones de entorno
export const getAllEnvironments = async () => {
  try {
    // Asegurar que la tabla esté inicializada
    await initializeEnvironmentTable();

    const allEnvs = await db.environment.toArray();
    return allEnvs.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
  } catch (error) {
    registro.error("Error al obtener configuraciones de entorno:", error);
    throw error;
  }
};

// Eliminar configuración de entorno
export const deleteEnvironment = async (id) => {
  try {
    const env = await db.environment.get(id);
    if (env && env.isActive) {
      throw new Error("No se puede eliminar la configuración activa");
    }
    await db.environment.delete(id);
    return true;
  } catch (error) {
    registro.error("Error al eliminar configuración de entorno:", error);
    throw error;
  }
};
